// Plan-feature hooks (docs/plan-limits-plan.md §7).
//
// GET /auth/me carries `plan.features` — the business's effective features
// (plan ∪ extra_features). A feature missing from it is locked: its screens stay
// visible with a lock and open on LockedFeature instead of the page.
//
// Platform users get `plan: null` and are never locked. A profile with no plan
// at all (not loaded yet, or an older API) locks nothing either — the API still
// refuses whatever the plan does not include.

import { useCallback } from 'react';
import type { PlanFeatureKey } from '@bbpos/types';
import { useAuthStore } from '@/stores/use-auth-store';

export type HasPlanFeature = (key: PlanFeatureKey) => boolean;

/** Whether each feature is in this user's plan. */
export function usePlanFeatures(): HasPlanFeature {
  const features = useAuthStore((s) => s.profile?.plan?.features);
  return useCallback((key: PlanFeatureKey) => !features || features.includes(key), [features]);
}

/** Whether one feature is in this user's plan. */
export function usePlanFeature(key: PlanFeatureKey): boolean {
  return usePlanFeatures()(key);
}
